import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useInventoryCategories } from "@/lib/inventory";

/**
 * Multi-select picker for inventory categories. Used in the Scope tab
 * of `<CustomFieldPanel />` when editing inventory custom fields.
 *
 * Empty selection = no scope = field applies to every item.
 */
export function InventoryCategoryScopePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const q = useInventoryCategories();
  const all = q.data ?? [];
  const selected = new Set(value);

  const toggle = (cat: string) => {
    if (selected.has(cat)) {
      onChange(value.filter((c) => c !== cat));
    } else {
      onChange([...value, cat]);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Apply to categories
      </p>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((cat) => (
            <span
              key={cat}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs"
            >
              {cat}
              <button
                type="button"
                onClick={() => toggle(cat)}
                aria-label={`Remove ${cat}`}
                className="hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            type="button"
            className="w-full justify-between"
          >
            <span className="text-muted-foreground">
              {value.length === 0
                ? "Pick categories…"
                : `${value.length} selected`}
            </span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
          <Command>
            <CommandInput placeholder="Search categories…" />
            <CommandList>
              <CommandEmpty>No categories yet. Categorize an item first.</CommandEmpty>
              <CommandGroup>
                {all.map((cat) => {
                  const isOn = selected.has(cat);
                  return (
                    <CommandItem
                      key={cat}
                      value={cat}
                      onSelect={() => toggle(cat)}
                    >
                      <Check
                        className={`mr-2 h-3.5 w-3.5 ${
                          isOn ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <span>{cat}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
